import i18n from 'i18next';
import ICU from 'i18next-icu';
import XHR from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import {initReactI18next} from 'react-i18next';
import myLanguageDetector from './myLanguageDetector';
import nl from 'dayjs/locale/nl';
import en from 'dayjs/locale/en';
import de from 'dayjs/locale/de';
import es from 'dayjs/locale/es';
import tr from 'dayjs/locale/tr';
import fr from 'dayjs/locale/fr';
import da from 'dayjs/locale/da';
import it from 'dayjs/locale/it';
import cs from 'dayjs/locale/cs';
import se from 'dayjs/locale/se';
import * as flags from './app/images/flags';
import * as env from './app/env';
import V4API from './app/api/V4client';

import {csCZ, deDE, enUS, esES, frFR, nlNL, trTR, svSE, daDK, itIT} from '@mui/material/locale';
import {
    csCZ as csCZ2,
    daDK as daDK2,
    deDE as deDE2,
    enUS as enUS2,
    esES as esES2,
    frFR as frFR2,
    itIT as itIT2,
    nlNL as nlNL2,
    trTR as trTR2,
    svSE as svSE2
} from '@mui/x-data-grid/locales';
import {consoleLog} from "./app/utils/log";
import {camelizeTranslationKey} from "./app/utils/translationKey";
import {setDateTimeLocale} from "./app/utils/time";

const isEnabled = () => true;

export const languages = [
    {name: 'Česky', lang: 'cs-CZ', dayjs: cs, key: 'cs', flag: flags.cs, isEnabled, mui: csCZ, muiDataGrid: csCZ2},
    {name: 'Dansk', lang: 'da-DK', dayjs: da, key: 'da', flag: flags.da, isEnabled, mui: daDK, muiDataGrid: daDK2},
    {name: 'Deutsch', lang: 'de-DE', dayjs: de, key: 'de', flag: flags.de, isEnabled, mui: deDE, muiDataGrid: deDE2},
    {name: 'English', lang: 'en-GB', dayjs: en, key: 'en', flag: flags.gb, isEnabled, mui: enUS, muiDataGrid: enUS2, isDefault: true},
    {name: 'Español', lang: 'es-ES', dayjs: es, key: 'es', flag: flags.es, isEnabled, mui: esES, muiDataGrid: esES2},
    {name: 'Français', lang: 'fr-FR', dayjs: fr, key: 'fr', flag: flags.fr, isEnabled, mui: frFR, muiDataGrid: frFR2},
    {name: 'Italiano', lang: 'it-IT', dayjs: it, key: 'it', flag: flags.it, isEnabled, mui: itIT, muiDataGrid: itIT2},
    {name: 'Nederlands', lang: 'nl-NL', dayjs: nl, key: 'nl', flag: flags.nl, isEnabled, mui: nlNL, muiDataGrid: nlNL2},
    {name: 'Türk', lang: 'tr-TR', dayjs: tr, key: 'tr', flag: flags.tr, isEnabled, mui: trTR, muiDataGrid: trTR2},
    {name: 'Swedish', lang: 'sv-SE', dayjs: se, key: 'sv', flag: flags.se, isEnabled, mui: svSE, muiDataGrid: svSE2},
];
export const getLocaleInfo = key => languages.find(l => l.key === key || l.lang === key) || getLocaleInfo('en-GB');

export const locales = Object.fromEntries(
    languages.map(l => [l.key, l.lang])
);

export const DEFAULT_LOCALE = locales.en;

const languageDetector = new LanguageDetector();
languageDetector.addDetector(myLanguageDetector);

let muteMissingKey = false;
const missingKeys = {};

// Map i18next's short locale code (e.g. 'en', 'cs') — which is what the rest of
// the app uses because of `load: 'languageOnly'` — to the BCP-47 code the V4
// backend stores (e.g. 'en-GB', 'cs-CZ'). Falls through if already full, and
// returns null for unknown codes (e.g. i18next's internal 'dev') so the request
// can be short-circuited without hitting the backend.
const toV4LocaleCode = (requested) => {
    if (!requested) return null;
    const match = languages.find(l => l.key === requested || l.lang === requested);
    return match ? match.lang : null;
};

i18n
    .use(languageDetector)
    .use(new ICU({
        localeData: languages.map(l => l.dayjs)
    }))
    .use(XHR)
    .use(initReactI18next)
    .init({
        backend: {
            // The `request` function below ignores this URL — `loadPath` is only used
            // to surface the requested lng to our request handler.
            loadPath: (lngs) => Array.isArray(lngs) ? lngs[0] : lngs,
            request: async (_options, url, _payload, callback) => {
                const requested = decodeURIComponent(url);
                const code = toV4LocaleCode(requested);
                if (!code) {
                    // Unknown lng (e.g. i18next's 'dev' fallback) — resolve with an
                    // empty namespace so i18next stops retrying.
                    callback(null, {status: 200, data: '{}'});
                    return;
                }
                try {
                    const res = await V4API.localesDetail(code);
                    const flat = {};
                    // Normalise backend keys to camelCase (matches lookup side).
                    (res?.data?.translations || []).forEach(({key, value}) => {
                        if (!key) return;
                        const normalizedKey = camelizeTranslationKey(key);
                        if (!env.isLive() && flat[normalizedKey] !== undefined && flat[normalizedKey] !== value) {
                            consoleLog(`translation key collision after camelCase normalisation -> "${normalizedKey}"`);
                        }
                        flat[normalizedKey] = value;
                    });
                    callback(null, {status: 200, data: JSON.stringify(flat)});
                } catch (e) {
                    callback(e, {status: e?.response?.status || 500, data: ''});
                }
            },
        },
        load: 'languageOnly',
        // Disable i18next's default 'dev' fallback — we don't want extra GETs for
        // languages the V4 backend doesn't know about.
        fallbackLng: false,
        debug: false,
        ns: ['translations'],
        defaultNS: 'translations',
        saveMissing: true,
        missingKeyHandler: async (lang, ns, key) => {
            if (key && key !== 'false') {
                if (!env.isLive()) {
                    if (missingKeys[key]) return;
                    missingKeys[key] = true;
                    consoleLog(`missing keys: ${Object.keys(missingKeys)} for lang ${lang}`);
                    if (muteMissingKey) return;
                    muteMissingKey = true;
                    window.alert(`missing key: ${key} for lang ${lang}`);
                    setTimeout(() => muteMissingKey = false, 5000);
                }
            }
        },
        keySeparator: false,
        detection: {
            order: ['myLanguageDetector', 'navigator']
        },
        nonExplicitWhitelist: true,
        interpolation: {
            escapeValue: false,
        },
        react: {
            useSuspense: true
        },
    });

// Normalise every lookup key to camelCase. translator.translate is the chokepoint
// for i18n.t / useTranslation / getFixedT; translator.exists backs i18n.exists().
const patchTranslatorForCamelCaseKeys = () => {
    const translator = i18n.translator;
    if (!translator || translator.__camelizePatched) return;
    const normalizeKeys = (keys) =>
        Array.isArray(keys) ? keys.map(camelizeTranslationKey) : camelizeTranslationKey(keys);
    const originalTranslate = translator.translate.bind(translator);
    translator.translate = (keys, options, lastKey) =>
        originalTranslate(normalizeKeys(keys), options, lastKey);
    const originalExists = translator.exists.bind(translator);
    translator.exists = (key, options) => originalExists(camelizeTranslationKey(key), options);
    translator.__camelizePatched = true;
};
patchTranslatorForCamelCaseKeys();
i18n.on('initialized', patchTranslatorForCamelCaseKeys);

// Dates follow the UI language; the formatter needs its full tag ('en' -> 'en-GB').
i18n.on('languageChanged', lng => setDateTimeLocale(getLocaleInfo(String(lng || '').split('-')[0]).lang));

export default i18n;